import React, {useContext, useRef, useState} from "react";
import TournamentRegistrationConfirmationModal from "../modals/TournamentRegistrationConfirmationModal";
import ServiceClient from "../utils/ServiceClient";
import {StoreContext} from "./StoreContextProvider";
import AdditionalSurveyQuestions from "../modals/AdditionalSurveyQuestions";
import {
  Tournament, TournamentJoinOptions, TournamentPlayerBet_State, TournamentPlayerDetails_State, TournamentState,
  TournamentSurveyQuestionType,
} from "../../generated/smg_service_pb";
import Div from "../lib/Div";
import P from "../lib/P";
import {colors} from "../assets/styles/colors";
import * as Yup from "yup";
import {useFormik} from "formik";
import moment from "moment";
import Button from "./Button";
import {useNavigation} from "@react-navigation/native";
import Btn from "./Btn";
import {isFullSwing} from "../constants";
import FullSwingInfoModal from "../modals/FullSwingInfoModal";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";

type Props = {
  onReload: () => void, leave?: () => void, hideLeaveButton?: boolean, tournament: Tournament,
  isTournamentSeries?: boolean
};

const JoinTournamentButton = (props: Props) => {
  const {tournament, hideLeaveButton, isTournamentSeries} = props;
  const {
    entryFee: entryfee, id, totalShots, currentPlayerDetails, state, playerDetails, questions: questionsList
  } = tournament;
  const registered = !!currentPlayerDetails;
  const joinDisabled = state === TournamentState.CANCELLED || state === TournamentState.FINISHED || state === TournamentState.VOIDED || currentPlayerDetails?.state === TournamentPlayerDetails_State.FINISHED;
  const shotsCompleted = playerDetails.find(el => el.playerId === currentPlayerDetails?.playerId)?.bets.filter(bet => bet.state === TournamentPlayerBet_State.FINISHED).length === totalShots;
  const autoPlayAmount = currentPlayerDetails?.betEntreeFee;
  const Context = useContext(StoreContext);
  const {cancelModalConfig} = Context;
  const [tournamentRegistrationModalType, setTournamentRegistrationModalType] = useState<'RegistrationConfirmationModal' | 'LowBalanceModal' | 'CancelCashChallenge' | 'LeaveTournament' | ''>('');
  const [showAdditionalSurveyQuestions, setShowAdditionalSurveyQuestions] = useState(false);
  const pendingJoinWithTicketRef = useRef(false);
  const initialValues: Record<string, string[]> = questionsList.reduce((values, question) => {
    values[question.id] = [];
    return values;
  }, {} as Record<string, string[]>);
  const validationSchema = Yup.object(
    questionsList.reduce((schema, question) => {
      if (question.type === TournamentSurveyQuestionType.MULTI_CHOICE) {
        schema[question.id] = Yup.array().min(1, 'Please select at least one option').required('This field is required');

      } else {
        schema[question.id] = Yup.array().of(Yup.string()).test('is-single-value', 'This field is required', (value) => Array.isArray(value) && value.length === 1 && !!value[0]).required('This field is required');
      }
      return schema;
    }, {} as Record<string, any>)
  );
  const formik = useFormik<Record<string, string[]>>({
    initialValues, validationSchema,
    onSubmit: () => {
      setShowAdditionalSurveyQuestions(false);
      if (pendingJoinWithTicketRef.current) {
        pendingJoinWithTicketRef.current = false;
        joinTournament('0', true);
        return;
      }
      setTournamentRegistrationModalType('RegistrationConfirmationModal');
    }
  });
  const notStarted = moment().diff(moment.unix(Number(tournament.entryBeginDate)), 'seconds') < 0;
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const [infoModal, setInfoModal] = useState({visible: false, title: '', subtitle: '', ticketIcon: false});

  const openDetails = () => {
    if (!isTournamentSeries) {
      navigation.navigate('TournamentDetails', {id: tournament.id, isPrivateTournament: tournament.isPrivate});
    }
  };

  const handleJoinButtonClick = (join_with_ticket?: boolean) => {
    if (questionsList.length) {
      pendingJoinWithTicketRef.current = !!join_with_ticket;
      setShowAdditionalSurveyQuestions(true);
      return;
    }

    if (join_with_ticket) {
      joinTournament('0', true);
    } else if (isFullSwing) {
      joinTournament('0');
    } else {
      setTournamentRegistrationModalType('RegistrationConfirmationModal');
    }
  };

  const LeaveTournament = () => {
    if (!shotsCompleted) setTournamentRegistrationModalType('LeaveTournament');
  };

  const enableAutoPlay = (fee: number) => {
    Context.setLoadingOverlay(true);
    setTournamentRegistrationModalType('');

    ServiceClient.enableAutoPlay(true, fee).then(() => {
      Context.setLoadingOverlay(false);
      props.onReload();

    }).catch(err => {
      console.log('enableAutoPlay err', err);
      Context.setLoadingOverlay(false);

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    });
  };

  const joinTournament = (val: string, join_with_ticket?: boolean) => {
    if (!id) return;
    Context.setLoadingOverlay(true);
    setTournamentRegistrationModalType('');

    ServiceClient.joinTournament(id, +val * 100, formik.values, join_with_ticket).then(() => {
      setTimeout(() => {
        Context.setLoadingOverlay(false);
        props.onReload();
      }, 500);

    }).catch(err => {
      console.log('joinTournament err', err);
      Context.setLoadingOverlay(false);

      if (err.message === 'insufficient balance') {
        setTournamentRegistrationModalType('LowBalanceModal');

      } else if (err.code === 20020043 && isFullSwing) {
        setInfoModal({
          visible: true, title: 'No qualification ticket', ticketIcon: true,
          subtitle: 'You do not have a qualifying ticket for this event.'
        });

      } else {
        Context.setCancelModalConfig({
          visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
        });
      }
    });
  };

  return (
    <>
      {tournament.state === TournamentState.FINISHED || tournament.state === TournamentState.CANCELLED || tournament.state === TournamentState.VOIDED ? (
        <>
          {isFullSwing ? (
            <Btn w={370} rounded={4} bg="#3369FF" h={60} column justifyContent="center" onPress={openDetails}>
              <P fontFamily="DavisSans-Medium" ta="center">SHOTS COMPLETED</P>
              <P fontFamily="DavisSans-Regular" fs={14} ta="center">See Results</P>
            </Btn>
          ) : (
            <Button
              text="Completed - See Results" w={370} h={60} fontWeight="600" onPress={openDetails} bg="transparent"
              borderWidth={1} borderColor="white" color="white" mx="auto" mb={10}
            />
          )}
        </>
      ) : notStarted ? (
        <Button
          text="Not Available to Join Yet" w={370} h={60} fontWeight="600" bg={isFullSwing ? '#BBBCBF' : 'transparent'}
          borderWidth={1} borderColor="white" color={isFullSwing ? '#494A4C' : 'white'} mx="auto" mb={10}
          radius={isFullSwing ? 2 : undefined}
        />
      ) : registered ? (
        <Div row w={370} mx="auto" mb={10}>
          {isFullSwing ? (
            <P lh={60} mx="auto">{shotsCompleted ? 'Live - See Results' : 'JOINED'}</P>
          ) : (
            <Button
              text={shotsCompleted ? 'Live - See Results' : 'Entered - View Results'} flex={1} h={60} fontSize={20}
              fontWeight="600" onPress={openDetails} radius={isFullSwing ? 4 : undefined} maxH={60}
              bg={isFullSwing ? '#3369FF' : undefined} color={isFullSwing ? 'white' : undefined}
            />
          )}

          {!hideLeaveButton && (
            <>
              <Div w={10}/>
              <Button
                text="Leave" flex={1} h={60} fontWeight="600" onPress={LeaveTournament} maxH={60}
                bg={isFullSwing ? '#252526' : 'transparent'} borderWidth={1}
                borderColor={isFullSwing ? '#4C4C4C' : 'white'} color="white" disabled={shotsCompleted}
                radius={isFullSwing ? 4 : undefined}
              />
            </>
          )}
        </Div>
      ) : (
        <>
          {isTournamentSeries ? (
            <>
              {moment.unix(Number(tournament.entryEndDate)).isAfter() && (
                <Div w={370} row justifyContent="space-between" mx="auto" mb={10}>
                  {(tournament.joinOptions === TournamentJoinOptions.ONLY_TICKET || tournament.joinOptions === TournamentJoinOptions.MONEY_OR_TICKET || tournament.joinOptions === TournamentJoinOptions.MONEY_AND_TICKET) && (
                    <>
                      <Btn flex={1} rounded={4} bg="#3369FF" h={60} alignItems="center" column justifyContent="center"
                           onPress={() => handleJoinButtonClick(true)}>
                        <P fontFamily="DavisSans-Medium" ta="center">Join</P>
                        <P fontFamily="DavisSans-Regular" fs={14} ta="center">with Ticket</P>
                      </Btn>

                      <Div w={13}/>
                    </>
                  )}

                  {(tournament.joinOptions === TournamentJoinOptions.ONLY_MONEY || tournament.joinOptions === TournamentJoinOptions.MONEY_OR_TICKET || tournament.joinOptions === TournamentJoinOptions.MONEY_AND_TICKET) && (
                    <Btn flex={1} rounded={4} bg="#1E9428" h={60} alignItems="center" column justifyContent="center"
                         onPress={() => handleJoinButtonClick()}>
                      {entryfee ? (
                        <>
                          <P fontFamily="DavisSans-Medium" ta="center">Join</P>
                          <P fontFamily="DavisSans-Regular" fs={14} ta="center">with Money</P>
                        </>
                      ) : (
                        <P fontFamily="DavisSans-Medium" ta="center">JOIN NOW</P>
                      )}
                    </Btn>
                  )}
                </Div>
              )}
            </>
          ) : (
            <Btn mx="auto" w={370} h={63} alignItems="center" rounded={isFullSwing ? 4 : 70}
                 opacity={joinDisabled ? 0.6 : undefined} bg={isFullSwing ? '#3369FF' : colors.newGreenText}
                 justifyContent="center" mb={14} disabled={joinDisabled} onPress={() => handleJoinButtonClick()}>
              <P color={isFullSwing ? 'white' : colors.mainBackground} fw="600" fs={22}>Join</P>

              <P color={isFullSwing ? 'white' : colors.mainBackground} fw="500" fs={18}>
                ${entryfee ? entryfee / 100 : 0} Entry Fee
              </P>
            </Btn>
          )}
        </>
      )}

      <TournamentRegistrationConfirmationModal
        isOpen={!!tournamentRegistrationModalType}
        onClose={() => setTournamentRegistrationModalType('')}
        joinTournament={val => {
          if (tournamentRegistrationModalType === 'CancelCashChallenge') {
            enableAutoPlay(0);

          } else if (tournamentRegistrationModalType === 'LeaveTournament') {
            setTournamentRegistrationModalType('');
            if (props.leave) props.leave();

          } else {
            joinTournament(val);
          }
        }}
        type={tournamentRegistrationModalType}
        totalShots={totalShots}
        entryFee={entryfee || 0}
        helperText={tournamentRegistrationModalType !== 'CancelCashChallenge' && tournamentRegistrationModalType !== 'LeaveTournament' ? 'Note: Your Auto-Play will be stopped during a Tournament until you have completed your Tournament Shots or the Tournament Time has ended.' : ''}
        entryChallengeFee={`${(autoPlayAmount || 0) / 100}`}
      />

      <AdditionalSurveyQuestions
        visible={showAdditionalSurveyQuestions}
        onClose={() => {
          setShowAdditionalSurveyQuestions(false);
          pendingJoinWithTicketRef.current = false;
        }}
        questionsList={questionsList} formik={formik}
      />

      <FullSwingInfoModal
        info={infoModal}
        onRequestClose={() => setInfoModal({visible: false, title: '', subtitle: '', ticketIcon: false})}
      />
    </>
  );
};

export default React.memo(JoinTournamentButton);
